import { utils } from '@hh/global.js'

const { debounce } = utils

class HHFiltersWrapper extends HTMLElement {
  constructor() {
    super()
    this.filterData = []
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this)
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event)
    }, 500)
    this.bindDebouncedSubmit = this.debouncedOnSubmit.bind(this)
    this.onHistoryChange = this.onHistoryChange.bind(this)
  }

  connectedCallback() {
    this.type = this.dataset.type
    this.isDesktop = this.dataset.isDesktop == 'true'
    this.selector = this.isDesktop
      ? '.js-facets--desktop'
      : '.js-facets--mobile'

    if (this.type === 'single') {
      this.querySelector('form').addEventListener(
        'input',
        this.bindDebouncedSubmit
      )

      this.initViewAll()
    } else {
      document.addEventListener('filterChosen', this.bindDebouncedSubmit)
    }

    window.addEventListener('popstate', this.onHistoryChange)

    this.bindActiveFacetButtonEvents()
  }

  disconnectedCallback() {
    if (this.type === 'single') {
      this.querySelector('form').removeEventListener(
        'input',
        this.bindDebouncedSubmit
      )
    } else {
      document.removeEventListener('filterChosen', this.bindDebouncedSubmit)
    }

    window.removeEventListener('popstate', this.onHistoryChange)
  }

  initViewAll() {
    const isShowingAll = window?.location?.href?.indexOf('filter') < 0
    document.querySelectorAll('.js-all-filters').forEach(el => el.classList[isShowingAll ? 'add' : 'remove']('filter__single--active'))
  }

  onSubmitHandler(event) {
    event.preventDefault()
    let formData
    if (this.type === 'single') {
      formData = new FormData(event.target.closest('form'))
    } else {
      formData = new FormData(event.detail.value.closest('form'))
    }
    const searchParams = new URLSearchParams(formData).toString()
    this.renderPage(searchParams, event)
  }

  onActiveFilterClick(event) {
    event.preventDefault()
    this.toggleActiveFacets()
    const param_value = event.target.dataset.input
    const param_name = event.target.dataset.name
    this.querySelector(
      `.js-combo-option input[value='${param_value}'][name='${param_name}']`
    )?.click()
  }

  onHistoryChange(event) {
    const searchParams = event.state?.searchParams || ''
    this.renderPage(searchParams, null, false)
  }

  toggleActiveFacets(disable = true) {
    document
      .querySelectorAll(`${this.selector} .js-facet-remove`)
      .forEach((element) => {
        element.classList.toggle('disabled', disable)
      })
  }

  renderPage(searchParams, event, updateURLHash = true) {
    document
      .getElementById('CollectionProductGrid')
      .querySelector('.js-product-grid')
      .classList.add('loading')

    const urlPrams = []

    new URLSearchParams(searchParams).forEach((value, key) => {
      if (value !== 'all') urlPrams.push(`${key}=${value}`)
    })

    const url =
      `${window.location.pathname}?` +
      this.getSections()
        .map((section) => `sections[]=${section.section}`)
        .join('&') +
      `&${urlPrams.join('&')}`

    const filterDataUrl = (element) => element.url === url

    this.filterData.some(filterDataUrl)
      ? this.renderSectionFromCache(filterDataUrl, event)
      : this.renderSectionFromFetch(url, event)

    if (updateURLHash) this.updateURLHash(urlPrams.join('&'))
  }

  renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.json())
      .then((parsed) => {
        const html = Object.values(parsed).join('')
        this.filterData = [...this.filterData, { html, url }]
        this.renderFilters(html, event)
        this.renderProductGrid(html)
      })
  }

  renderSectionFromCache(filterDataUrl, event) {
    const html = this.filterData.find(filterDataUrl).html
    this.renderFilters(html, event)
    this.renderProductGrid(html)
  }

  renderProductGrid(html) {
    const innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('CollectionProductGrid').innerHTML

    document.getElementById('CollectionProductGrid').innerHTML = innerHTML
  }

  renderFilters(...args) {
    this.isDesktop
      ? this.renderSingleFormFilters('#CollectionFiltersForm', ...args)
      : this.renderSingleFormFilters('#CollectionFiltersFormMobile', ...args)

    const isShowingAll = window?.location?.href?.indexOf('filter') < 0
    document
      .querySelector('.js-all-filters')
      ?.classList[isShowingAll ? 'add' : 'remove']('filter__single--active')
  }

  renderSingleFormFilters(form, html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')

    const facetDetailsElements = parsedHTML.querySelectorAll(
      `${form} .js-filter`
    )
    const matchesIndex = (element) =>
      element.dataset.index ===
      event?.target.closest('.js-filter')?.dataset.index

    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element)
    )

    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex)

    facetsToRender.forEach((element) => {
      document.querySelector(
        `${form} .js-filter[data-index="${element.dataset.index}"]`
      ).innerHTML = element.innerHTML
    })

    this.renderActiveFacets(parsedHTML)

    if (countsToRender) {
      this.renderCounts(countsToRender, event.target.closest('.js-filter'))
    }
  }

  renderActiveFacets(html) {
    const activeFacetsElement = html.querySelector(this.selector)
    if (!activeFacetsElement) return
    document.querySelector(this.selector).innerHTML =
      activeFacetsElement.innerHTML

    this.bindActiveFacetButtonEvents()
    this.toggleActiveFacets(false)
  }

  renderCounts(source, target) {
    const countElementSelectors = ['.js-bubble', '.js-selected']
    countElementSelectors.forEach((selector) => {
      const targetElement = target.querySelector(selector)
      const sourceElement = source.querySelector(selector)

      if (sourceElement && targetElement) {
        target.querySelector(selector).outerHTML =
          source.querySelector(selector).outerHTML
      }
    })
  }

  bindActiveFacetButtonEvents() {
    document
      .querySelectorAll(`${this.selector} .js-facet-remove`)
      .forEach((element) => {
        element.addEventListener('click', this.onActiveFilterClick, {
          once: true,
        })
      })
  }

  updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
    )
  }

  getSections() {
    return [
      {
        id: 'main-collection-filters',
        section: document.getElementById('main-collection-filters').dataset.id,
      },
      {
        id: 'main-collection-product-grid',
        section: document.getElementById('main-collection-product-grid').dataset
          .id,
      },
    ]
  }
}

customElements.define('hh-filters-wrapper', HHFiltersWrapper)
